<template>
	<div :class='"HorizontalProgressBar " + actorProperty.property.color'>
		<div :class="propertyInfoBarClasses">
			<div v-if="iconUrl" class="PropertyBarInfo__icon pull-left">
				<img :src="iconUrl">
			</div>
			<div v-if="!iconUrl" class="property-name pull-left">
				{{ propertyName }}
			</div>
			<div class="PropertyBarInfo__value-text pull-right text-right">
				<span
					v-for="(number, index) in actorPropertyNumbers"
					:key="`number-${index}-${number}`"
					:class='(phaseCurrentValue != number ? "PropertyBarInfo__value-text--previewing" : "")'
				>
					<span
						v-if="index > 0 && number > 0 && actorPropertyNumbers.length > 1"
						>max.{{ " " }}</span
					>
					<animated-number
						v-if="(index > 0 && actorPropertyNumbers.length > 1) || (actorProperty.preview_values == null && actorPropertyNumbers.length > 1)"
						:prev-number="actorProperty.old_current_value || phaseCurrentValue"
						:number="number"
						:animation-duration="((actorProperty.preview_values || animateNumbers) ? maxAnimation : 0)"
						:value-units="valueUnits"
					></animated-number>
					<animated-number
						v-if="actorPropertyNumbers.length == 1 || actorProperty.preview_values == null"
						:prev-number="actorProperty.old_current_value || phaseCurrentValue"
						:number="number"
						:value-units="valueUnits"
						:animation-duration="((actorProperty.preview_values || animateNumbers) ? maxAnimation : 0)"
					></animated-number>
				</span>
				<span
					class="property-goal-value-text"
					v-if="showMaxNumber && actorPropertyGoal && actorPropertyGoal < Math.pow(2, 32)"
				>
					/
					<span
						:class='highlightGoalNumbers == true ? "PropertyBarInfo__value-text--highlighted" : ""'
					>
						<animated-number
							:number="parseInt(actorPropertyGoal.toFixed())"
							:animation-duration="maxAnimation"
							:value-units="valueUnits"
						></animated-number>
					</span>
				</span>
			</div>
		</div>
		<div :class="propertyValueBarClasses">
			<span class="PropertyValueBar__goal-met-icon">
				<img src="/img/unionslabor/icons/check.svg">
			</span>
			<div class="PropertyValueBar__value" :style="propertyBarStyles()"></div>
			<div
				class="PropertyValueBar__value-range"
				:style="previewRangeStyles()"
			></div>
		</div>
	</div>
</template>

<script>
// Other components
import { i18n } from "../utils";
import AnimatedNumber from "./animated_number.vue";

export default {
	// Naming - server = srv
	mixins: [i18n],
	props: {
		srvPropertyTypeKey: String,
		srvPhaseId: Number,
		actorId: String,
		actorPropertyGoal: Number,
		iconUrl: String,
		showMaxNumber: {
			type: Boolean,
			default: true,
		},
		animateNumbers: {
			type: Boolean,
			default: false,
		},
	},
	data: function () {
		return {
			maxAnimation: 1.33,
			highlightGoalNumbers: false,
			locale: I18n.locale,
		};
	},
	components: {
		"animated-number": AnimatedNumber,
	},
	computed: {
		actorProperty: function () {
			// pp.log("getting", this.actorId, this.srvPropertyTypeKey, VueStore.getters.actorPropertyOfTypeFor(this.actorId, this.srvPropertyTypeKey))
			return VueStore.getters.actorPropertyOfTypeFor(
				this.actorId,
				this.srvPropertyTypeKey,
			);
		},
		phaseId: function () {
			return this.srvPhaseId !== undefined
				? this.srvPhaseId
				: pp.gameState.phaseId;
		},
		phaseCurrentValue: function () {
			return this.phaseId !== undefined &&
				this.actorProperty.values[this.phaseId] !== undefined
				? this.actorProperty.values[this.phaseId].current_value
				: 0;
		},
		propertyValueAsPercentOfGoal: function () {
			if (
				this.actorPropertyGoal == undefined ||
				this.actorPropertyGoal == null ||
				this.actorPropertyGoal == 0
			) {
				return 0;
			}
			return (this.phaseCurrentValue / this.actorPropertyGoal) * 100;
		},
		actorPropertyNumbers: function () {
			const previewValues = this.actorProperty.preview_values;
			if (this.actorProperty.old_current_value || !previewValues) {
				return [this.phaseCurrentValue];
			} else if (previewValues.single_value) {
				return [this.phaseCurrentValue + previewValues.single_value];
			}
			// else
			return [
				this.phaseCurrentValue + previewValues.range[0],
				this.phaseCurrentValue + previewValues.range[1],
			];
		},
		goalMet: function () {
			const rightestPosOfBars =
				this.propertyValueOffset + this.propertyValueBarWidth;
			return (
				(this.actorPropertyGoal == 0 && this.phaseCurrentValue >= 0) ||
				rightestPosOfBars >= 100
			);
		},
		propertyValueBarWidth: function () {
			if (
				this.actorProperty.omit_transition &&
				this.actorProperty.preview_values
			) {
				return 100;
			}
			// Else
			return this.asPercentOfGoal(Math.floor(this.phaseCurrentValue));
		},
		propertyName: function () {
			return this.actorProperty.property.names[this.locale];
		},
		previewRangeWidth: function () {
			if (this.actorProperty.preview_values) {
				const range = this.actorProperty.preview_values.range,
					currVal = Math.floor(this.phaseCurrentValue);
				let width = 0;
				if (range[0] < 0 && range[1] >= 0) {
					width = range[1] + Math.abs(range[0]);
				} else if (range[0] > 0) {
					width = range[1];
				} else if (range[0] < 0) {
					width = range[0];
				}
				return this.asPercentOfGoal(width);
			}
			// Else
			return 0;
		},
		propertyValueOffset: function () {
			return this.phaseCurrentValue < 0 ? this.propertyValueBarWidth : 0;
		},
		previewRangeOffset: function () {
			const previewValues = this.actorProperty.preview_values;
			const currVal = this.actorProperty.old_current_value
				? Math.floor(this.actorProperty.old_current_value)
				: Math.floor(this.phaseCurrentValue);

			// if (this.actorProperty.old_current_value) pp.log("old value present:", this.actorProperty.old_current_value);
			let offset = 0;
			if (!previewValues || previewValues.range[0] > 0) {
				offset = currVal; // width is currVal to upper limit
			} else if (previewValues.range[0] < 0) {
				offset = currVal + previewValues.range[0]; // Is negative
			}
			return this.asPercentOfGoal(offset);
		},
		previewDefinitelyMetGoal: function () {
			const previewDefinitelyReachesGoal =
				this.previewRangeOffset +
					this.asPercentOfGoal(this.actorProperty.preview_values.range[0]) >=
				100;
			let tippingPoint = this.propertyValueOffset + this.propertyValueBarWidth; // gipfel preview pushes the value
			return tippingPoint >= 100 || previewDefinitelyReachesGoal;
		},
		propertyValueBarClasses: function () {
			let classes = "PropertyValueBar clearfix";
			if (this.actorProperty.preview_values) {
				if (this.previewDefinitelyMetGoal) {
					classes += " PropertyValueBar--preview-met-goal";
				}
			} else {
				if (
					this.goalMet ||
					this.actorPropertyGoal == undefined ||
					this.actorPropertyGoal == null
				) {
					classes += " PropertyValueBar--met-goal";
				}
			}
			return classes;
		},
		propertyInfoBarClasses: function () {
			let classes = "PropertyBarInfo ";
			if (!this.actorProperty.preview_values && this.phaseCurrentValue < 0) {
				classes += " PropertyBarInfo--negative-value";
			}
			if (this.iconUrl) {
				classes += " PropertyBarInfo--with-icon";
			}
			return classes;
		},
		valueUnits: function () {
			return this.actorProperty.property.value_units;
		},
	},
	mounted() {
		pp.vue.bus.$on("highlight-goal-numbers", this.onHighlightGoalNumbers);
	},
	beforeDestroy() {
		pp.vue.bus.$off("highlight-goal-numbers", this.onHighlightGoalNumbers);
	},
	methods: {
		onHighlightGoalNumbers() {
			this.highlightGoalNumbers = true;
			window.setTimeout(() => {
				this.highlightGoalNumbers = false;
			}, 3500);
		},
		previewRangeStyles() {
			const startingPos = `${this.previewRangeOffset}%`;
			// never show the preview when omit_transition is set (i.e. when a new goal as been set)
			// propertyValueBarWidth will set the width if the property bar too 100
			if (
				this.actorProperty.preview_values &&
				!this.actorProperty.omit_transition
			) {
				const stylesHash = {
					transitionDuration: `${this.maxAnimation}s`,
					width: `${Math.abs(this.previewRangeWidth)}%`,
					"border-left": "none",
					"border-right": "none",
				};
				stylesHash[I18n.t("meta.direction") == "rtl" ? "right" : "left"] =
					startingPos;
				return stylesHash;
			}
			// else
			const stylesHash = { transitionDuration: "0s", width: 0 };
			stylesHash[I18n.t("meta.direction") == "rtl" ? "right" : "left"] =
				startingPos;
			return stylesHash;
		},
		propertyBarStyles() {
			// Set to 0 if omit_transition is set and preview_values and old_current_value are not,
			// i.e. when `onNationalMeasureEnacted` has received a new goal, called markTransitionOmissionOfProperties
			// and *then* the VueStore has called updateActorsPropertiesFor,
			// which only preserves omit_transition, but not preview_values and old_current_value
			const transitionDuration =
				this.actorProperty.omit_transition &&
				!this.actorProperty.preview_values &&
				!this.actorProperty.old_current_value
					? "0s"
					: `${this.maxAnimation}s`;
			const stylesHash = {
				width: `${Math.abs(this.propertyValueBarWidth)}%`,
				transitionDuration: transitionDuration,
				zIndex: this.actorProperty.old_current_value ? 4 : 1,
			};
			stylesHash[I18n.t("meta.direction") == "rtl" ? "right" : "left"] =
				`${this.propertyValueOffset}%`;
			return stylesHash;
		},
		asPercentOfGoal(number) {
			if (!this.actorPropertyGoal) {
				return 0;
			}
			if (
				this.actorPropertyGoal == 0 &&
				this.actorProperty.preview_values &&
				this.actorProperty.preview_values.range[0] > 0
			) {
				return 100;
			}
			return (number / this.actorPropertyGoal) * 100;
		},
	},
};
</script>
